import { useUnistyles } from "react-native-unistyles";
import { StyleSheet } from "react-native";
import type { LazySnapshotProps } from "./LazySnapshot.types";

/**
 * Web implementation of `LazySnapshot`.
 *
 * Renders a native `<img>` with `loading="lazy"`, `decoding="async"`,
 * and `fetchpriority="low"` so the browser handles the
 * IntersectionObserver bookkeeping internally and we don't pay the
 * fetch/decode cost for the long tail of thumbnails the user never
 * scrolls to. A tinted `<div>` underlay reserves the layout slot so
 * the page doesn't reflow as images stream in.
 *
 * We deliberately avoid `expo-image` here: as of expo-image v3 the
 * web wrapper does not forward the standard `loading` attribute, and
 * a 2,000-thumbnail report needs the browser-native primitive — not a
 * userland IntersectionObserver per `<Image>` (which is what eager
 * `expo-image` falls back to on web).
 */
export function LazySnapshot({ uri, width, height, borderRadius = 0, alt, style }: LazySnapshotProps) {
  const { theme } = useUnistyles();
  // Caller-provided `style` is a React Native ImageStyle. Flatten it so
  // we can lift margin/opacity-style overrides onto the wrapper <div>
  // without letting consumers stomp on the size we've reserved.
  const flat = StyleSheet.flatten(style ?? {}) as Record<string, unknown>;

  return (
    <div
      style={{
        ...flat,
        position: "relative",
        width,
        height,
        borderRadius,
        overflow: "hidden",
        backgroundColor: theme.colors.grey[200],
        flexShrink: 0,
      }}
    >
      <img
        src={uri}
        alt={alt ?? ""}
        loading="lazy"
        decoding="async"
        // `fetchPriority` is React's camelCase form for the
        // `fetchpriority` HTML attribute. We mark these as low-priority
        // so they don't steal bandwidth from above-the-fold content
        // (charts, page chrome).
        fetchPriority="low"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />
    </div>
  );
}
